import type { SelectOption } from '@aus-platform/design-system';
import { isUndefined } from 'lodash';
import proj4 from 'proj4';
import { getErrorMessage } from 'src/map-3d/components/map-3d-sidebar/components/cross-section/utils/helpers';
import { TextCRSToEPSGCodeMapping } from 'src/processing-workflow/constants';
import * as utm from 'utm';
import { type CRSInfo } from '../api';
import {
  CRSMapping,
  horizontalCrsSridMapping,
  WKTCrsToNameMapping,
} from '../constants';
import {
  EPSGCode,
  Hemisphere,
  type VerticalCRS,
  WGS84Crs,
  WKTVersion,
} from '../enums';
import { type UTMCoordinates } from '../types';

export const getHorizontalAndVerticalCRS = (
  WKT: string,
): { horizontal: EPSGCode; vertical: VerticalCRS } => {
  const [horizontalCrsEpsg, VerticalCrs] = WKTCrsToNameMapping[WKT];
  return {
    horizontal: TextCRSToEPSGCodeMapping[horizontalCrsEpsg],
    vertical: VerticalCrs as VerticalCRS,
  };
};

export const formatToCRSMappingKey = (
  horizontalCRS: EPSGCode,
  verticalCRS: string,
) => {
  const horizontalCrsName = horizontalCrsSridMapping[horizontalCRS];
  return `${horizontalCrsName}+${verticalCRS}`;
};

export const getWktCrs = (horizontalCRS, verticalCRS) => {
  return CRSMapping[formatToCRSMappingKey(horizontalCRS, verticalCRS)];
};

export const detectWktCrsVersion = (wkt: string): string => {
  // Normalize the WKT string for easier keyword matching.
  const normalizedWKT = wkt.toUpperCase();
  const hasWkt2Keywords = [
    'PROJCRS',
    'GEOGCRS',
    'BOUNDCRS',
    'VERTCRS',
    'LENGTHUNIT',
    'ANGLEUNIT',
    'SCALEUNIT',
  ].some((keyword) => normalizedWKT.includes(keyword));

  // Check for WKT2-specific keywords.
  if (hasWkt2Keywords) {
    return WKTVersion.WKT_2;
  }

  const hasWkt1Keywords = [
    'PROJCS',
    'GEOGCS',
    'LOCAL_CS',
    'VERT_CS',
    'UNIT',
  ].some((keyword) => normalizedWKT.includes(keyword));

  // Check for WKT1-specific keywords.
  if (hasWkt1Keywords) {
    return WKTVersion.WKT_1;
  }

  return '';
};

export const formatCRSOptionLabel = (
  option: SelectOption,
  sourceCrs?: string,
) => {
  return (
    <span className="crs-label">
      {option.label}
      {option.value === sourceCrs && (
        <span style={{ fontStyle: 'italic' }}> - detected</span>
      )}
    </span>
  );
};

/**
 * Converts geographic coordinates (longitude, latitude) to UTM (Universal Transverse Mercator) coordinates.
 *
 * @param {number} longitude - The longitude value of the point.
 * @param {number} latitude - The latitude value of the point.
 * @param {number} epsgCode - The EPSG code of the coordinate reference system.
 * @param {number} [decimal=3] - The number of decimal places to include in the output coordinates.
 * @returns {UTMCoordinates} An object containing the UTM zone number and formatted easting/northing coordinates.
 */
export const getUTMCoordinates = (
  longitude: number,
  latitude: number,
  epsgCode: EPSGCode = EPSGCode.WGS84,
  decimal = 3,
): UTMCoordinates => {
  const isNorthernHemisphere = latitude >= 0;
  const hemisphere = isNorthernHemisphere ? Hemisphere.NORTH : Hemisphere.SOUTH;

  try {
    // Get zone number using utm library.
    const { zoneNum: zoneNumber } = utm.fromLatLon(latitude, longitude);

    // Use helper function to transform point to UTM
    const [easting, northing] = transformPoint(
      [longitude, latitude],
      epsgCode,
      zoneNumber,
      hemisphere,
      true,
    );

    return {
      zoneNumber,
      hemisphere,
      utmCoords: {
        easting: easting.toFixed(decimal),
        northing: northing.toFixed(decimal),
      },
    };
  } catch (error) {
    console.error(`UTM conversion failed: ${error}`);
    return {
      zoneNumber: -1,
      hemisphere: Hemisphere.UNKNOWN,
      utmCoords: {
        easting: '-',
        northing: '-',
      },
    };
  }
};

export const isProjectedCrs = (crsCode: number): boolean => {
  const epsg = `EPSG:${crsCode}`;
  const def = proj4.defs(epsg);

  return def?.units !== 'degrees';
};

/**
 * Gets the source projection string for a given EPSG code.
 * Handles cases where the EPSG code might not have a datum code defined.
 *
 * @param epsgCode - EPSG code
 * @returns Source projection string for proj4
 */
export const getSourceProjectionString = (epsgCode: EPSGCode): string => {
  const epsg = `EPSG:${epsgCode}`;
  const crsDef = proj4.defs[epsg];
  return crsDef?.datumCode ? epsg : `EPSG:${EPSGCode.WGS84}`;
};

/**
 * Builds a UTM projection string for a given zone and hemisphere.
 *
 * @param zoneNumber - UTM zone number (1-60)
 * @param hemisphere - Hemisphere ('N' or 'S')
 * @param epsgCode - Source EPSG code to determine datum
 * @returns UTM projection string for proj4
 */
export const buildUTMProjectionString = (
  zoneNumber: number,
  hemisphere: Hemisphere,
  epsgCode: EPSGCode = EPSGCode.WGS84,
): string => {
  const epsg = `EPSG:${epsgCode}`;
  const crsDef = proj4.defs[epsg];
  const datum = crsDef?.datumCode || WGS84Crs.name;
  return `+proj=utm +zone=${zoneNumber} ${hemisphere === Hemisphere.NORTH ? '+north' : '+south'} +datum=${datum} +units=m +no_defs`;
};

/**
 * Transforms a single point between geographic CRS and UTM.
 *
 * @param point - Point as [longitude, latitude] or [easting, northing]
 * @param epsgCode - EPSG code
 * @param zoneNumber - UTM zone number
 * @param hemisphere - Hemisphere ('N' or 'S')
 * @param toUTM - If true, transforms from geographic CRS to UTM; if false, transforms from UTM to geographic CRS
 * @returns Transformed point
 * @throws Error if transformation fails
 */
export const transformPoint = (
  point: [number, number],
  epsgCode: EPSGCode,
  zoneNumber: number,
  hemisphere: Hemisphere,
  toUTM: boolean,
): [number, number] => {
  try {
    const sourceProj = getSourceProjectionString(epsgCode);
    const utmProj = buildUTMProjectionString(zoneNumber, hemisphere, epsgCode);

    const [fromProj, toProj] = toUTM
      ? [sourceProj, utmProj]
      : [utmProj, sourceProj];

    const [x, y] = proj4(fromProj, toProj, [point[0], point[1]]);
    return [x, y];
  } catch (error) {
    const directionText = toUTM ? 'from EPSG to UTM' : 'from UTM to EPSG';
    console.error(
      `Failed to transform point [${point[0]}, ${point[1]}] ${directionText} (EPSG:${epsgCode}):`,
      error,
    );
    throw new Error(
      `Coordinate transformation failed. Cannot transform ${directionText}. ${error instanceof Error ? error.message : String(error)}`,
    );
  }
};

/**
 * Checks if a CRS definition exists in proj4.
 *
 * @param epsgCode - EPSG code to check
 * @returns true if the CRS is defined, false otherwise
 */
export const isCRSDefined = (epsgCode: number): boolean => {
  const epsg = `EPSG:${epsgCode}`;
  return !isUndefined(proj4.defs(epsg));
};

/**
 * Registers a CRS definition with proj4 from CRS info object.
 * Tries WKT first, falls back to proj4 string if present.
 *
 * @param crsInfo - CRS information from API containing wkt string
 * @returns void
 */
export const registerCRSFromInfo = (crsInfo: CRSInfo): void => {
  const epsg = `EPSG:${crsInfo.srid}`;

  if (isCRSDefined(crsInfo.srid)) {
    return;
  }

  try {
    proj4.defs(epsg, crsInfo.wkt);

    if (crsInfo.proj4) {
      proj4.defs(epsg, crsInfo.proj4);
    }
  } catch (error) {
    console.error(`Failed to register CRS ${epsg}:`, error);
    throw new Error(
      `Failed to register CRS ${epsg}: ${getErrorMessage(error)}`,
    );
  }
};
